/**
 * The permission vocabulary, and the role → permission matrix.
 *
 * This file is the single source of truth for authorization. The API enforces it
 * server-side on every request; the web app imports the same matrix only to decide
 * what to *render*. A client-side check is a UX affordance, never a control — the
 * server must re-derive the same answer independently for every call.
 *
 * Permissions read `resource:action`. Coarse enough to stay comprehensible, fine
 * enough that the dangerous verbs (remote commands, immobilizer, billing exports)
 * are separable from ordinary reads.
 */
export const ROLES = [
    'super_admin',
    'tenant_admin',
    'fleet_manager',
    'dispatcher',
    'viewer',
    'driver',
];
/** Ordered most- to least-privileged. Used for "can this user manage that user". */
export const ROLE_RANK = {
    super_admin: 100,
    tenant_admin: 80,
    fleet_manager: 60,
    dispatcher: 40,
    viewer: 20,
    driver: 10,
};
export const PERMISSIONS = [
    // Platform scope — only ever granted to super_admin.
    'platform:manage_tenants',
    'platform:impersonate',
    'platform:read_metrics',
    // Trackers the operator owns but has not yet sold. Above the tenant line by
    // construction — a box on a shelf belongs to no customer — so it is a platform
    // permission rather than a tenant one, and super_admin is the only role that holds it.
    'platform:manage_stock',
    // Fleet structure
    'fleets:read',
    'fleets:write',
    'vehicles:read',
    'vehicles:write',
    'vehicles:delete',
    // Devices and the remote command channel
    'devices:read',
    'devices:write',
    'devices:command', // Codec 12 — getinfo/getver/config reads
    'devices:immobilizer', // DOUT relay control. Can strand a vehicle. Kept separate.
    // People
    'drivers:read',
    'drivers:write',
    'users:read',
    'users:write',
    // Operational data
    'live:read',
    'trips:read',
    'trips:write', // manual correction of a mis-stitched trip
    'telemetry:read_raw',
    'fuel:read',
    'fuel:write', // manual fill-up entries, fuel-card CSV import
    'scoring:read',
    'scoring:configure', // per-tenant penalty weights
    'maintenance:read',
    'maintenance:write',
    'alerts:read',
    'alerts:acknowledge',
    'alerts:configure',
    'geofences:read',
    'geofences:write',
    'routes:read',
    'routes:write',
    'routes:optimize',
    'worksites:read',
    'worksites:write',
    'worksites:billing', // hour-meter statements used for rental invoicing
    // Output
    'reports:read',
    'reports:schedule',
    'dashboards:read',
    // Tenant administration
    'settings:read',
    'settings:write',
    'branding:write',
    'apikeys:manage',
    'webhooks:manage',
    'audit:read',
    // The driver self-service view: own trips and own score, nothing else.
    'self:read',
    /**
     * Submitting a walk-around (DVIR) — the ONE write a driver can perform.
     *
     * Separate from `self:read` because it is a write, and separate from
     * `maintenance:write` because a driver must be able to REPORT a fault without being
     * able to close one. Reporting and repairing are different people by design: a driver
     * who could mark their own defect repaired makes the whole record worthless.
     */
    'self:inspect',
];
const VIEWER_PERMISSIONS = [
    'fleets:read',
    'vehicles:read',
    'devices:read',
    'drivers:read',
    'live:read',
    'trips:read',
    'telemetry:read_raw',
    'fuel:read',
    'scoring:read',
    'maintenance:read',
    'alerts:read',
    'geofences:read',
    'routes:read',
    'worksites:read',
    'reports:read',
    'dashboards:read',
    'settings:read',
];
const DISPATCHER_PERMISSIONS = [
    ...VIEWER_PERMISSIONS,
    'alerts:acknowledge',
    'routes:write',
    'routes:optimize',
    'geofences:write',
    'worksites:write',
    'devices:command',
];
const FLEET_MANAGER_PERMISSIONS = [
    ...DISPATCHER_PERMISSIONS,
    'fleets:write',
    'vehicles:write',
    'devices:write',
    'drivers:write',
    'trips:write',
    'fuel:write',
    'maintenance:write',
    'alerts:configure',
    'reports:schedule',
    'worksites:billing',
    'scoring:configure',
];
const TENANT_ADMIN_PERMISSIONS = [
    ...FLEET_MANAGER_PERMISSIONS,
    'vehicles:delete',
    'users:read',
    'users:write',
    'settings:write',
    'branding:write',
    'apikeys:manage',
    'webhooks:manage',
    'audit:read',
    // Immobilizer control stops at tenant_admin. It is the one permission that can
    // leave a driver stranded on a road at night, so it is not part of the ordinary
    // fleet-manager bundle and has to be granted deliberately.
    'devices:immobilizer',
];
/**
 * `driver` is deliberately almost empty. A driver signing in sees their own trips
 * and their own score through the self-service endpoints and nothing else — not the
 * fleet list, not other drivers' scores. Scoping is enforced by the API resolving
 * "self" from the token subject, never from a client-supplied id.
 *
 * `self:inspect` is the single write, and it is deliberately not `maintenance:write`:
 * a driver REPORTS a fault, a mechanic CLOSES one. A driver able to mark their own
 * defect repaired would make the whole inspection record worthless.
 */
const DRIVER_PERMISSIONS = ['self:read', 'self:inspect'];
export const ROLE_PERMISSIONS = {
    super_admin: PERMISSIONS,
    tenant_admin: TENANT_ADMIN_PERMISSIONS,
    fleet_manager: FLEET_MANAGER_PERMISSIONS,
    dispatcher: DISPATCHER_PERMISSIONS,
    viewer: VIEWER_PERMISSIONS,
    driver: DRIVER_PERMISSIONS,
};
/**
 * Membership lookups are on the hot path of every authorized request, so the arrays
 * above are indexed once at module load. Built with an explicit loop rather than
 * Object.fromEntries because only the loop keeps `Role` as the key type.
 */
const ROLE_PERMISSION_SETS = (() => {
    const sets = {};
    for (const role of ROLES) {
        sets[role] = new Set(ROLE_PERMISSIONS[role]);
    }
    return sets;
})();
export function roleHasPermission(role, permission) {
    return ROLE_PERMISSION_SETS[role].has(permission);
}
/** A user may hold several roles; permissions are the union. */
export function permissionsForRoles(roles) {
    const out = new Set();
    for (const role of roles) {
        for (const permission of ROLE_PERMISSIONS[role] ?? [])
            out.add(permission);
    }
    return out;
}
export function hasPermission(roles, permission) {
    return roles.some((role) => roleHasPermission(role, permission));
}
export function hasAllPermissions(roles, required) {
    return required.every((permission) => hasPermission(roles, permission));
}
export function hasAnyPermission(roles, required) {
    return required.some((permission) => hasPermission(roles, permission));
}
/**
 * Whether `actor` is allowed to create, modify or delete a user holding `target`.
 * Strictly greater, not greater-or-equal: a tenant_admin must not be able to
 * delete a peer tenant_admin and lock the tenant out of its own account.
 */
export function canManageRole(actorRoles, target) {
    const actorRank = Math.max(...actorRoles.map((r) => ROLE_RANK[r] ?? 0), 0);
    return actorRank > ROLE_RANK[target];
}
export function isRole(value) {
    return ROLES.includes(value);
}
//# sourceMappingURL=permissions.js.map